/**
 * @file NarrativeOnlyEditor.tsx
 *
 * @description Schema-less editor shown while a report is in narrative-only
 * mode (the full schema-bound report has not been generated yet). It edits the
 * narrative(s) directly — no RJSF, no JSON schema — and exposes a "Generate
 * Full Report" action that triggers the agent to expand the narrative into the
 * full structured report.
 *
 * Reuses the same narrative primitives as NarrativeWidget (NarrativeEditor +
 * AIAssistanceTools + NarrativeConfirmationModal + useNarrativeRegeneration),
 * but persists edits via the dedicated PUT narrative API instead of the
 * WebSocket save path used by the full editor.
 */
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { VideoPreview } from "../../../components/video/VideoPreview";
import { useToast } from "../../../shared/toast";
import { useFormDataStore } from "../store/formDataStore";
import { useEditSection } from "../EditSectionContext";
import { ReportStatus } from "../../../shared/interfaces/ReportStatus";
import { extractNarrativeTexts } from "@/features/co-review/model/coReviewHelpers";
import { narrativeKey } from "@/features/reports/config/narrativeOnlySchema";
import { EforceIncidentModel } from "../../../shared/interfaces/EforceModels";

import { NarrativeEditor } from "../../../components/widgets/narrative/NarrativeEditor";
import { AIAssistanceTools } from "../../../components/widgets/narrative/AIAssistanceTools";
import {
  NarrativeConfirmationModal,
  OperationType,
} from "../../../components/widgets/narrative/NarrativeConfirmationModal";
import { useNarrativeRegeneration } from "../../../components/widgets/narrative/useNarrativeRegeneration";
import {
  getNarrativeEditorValue,
  reconcileNarrativeEditorChange,
} from "../../../components/widgets/narrative/narrativeEditorCitations";
import {
  SourceLinkedEditBar,
  SourceLinkedNarrative,
  useSourceLinkedSwap,
} from "../../../components/widgets/narrative/SourceLinkedNarrative";
import { useNarrativeCitationsEnabled } from "../../../components/chat/hooks/useNarrativeCitationsEnabled";
import { parseCitations } from "../../../features/narrative-citations/parseCitations";
import {
  LoadingOperation,
  RegenerationArgs,
  RegenerationType,
} from "../../../components/widgets/narrative/types";
import { ConfirmationCheckbox } from "./ConfirmationCheckbox";
import { FormSubmitArea } from "./FormSubmitArea";
import { FullReportGenerationProgress } from "./FullReportGenerationProgress";

// Narrative prose is stored under `Text` (eForce) or `Narrative` (KAR). Resolve
// the key per entry so write-back lands in the same field it was read from.
const resolveNarrativeKey = (
  entry: Record<string, unknown> | undefined,
  rms: string,
): "Text" | "Narrative" => {
  if (entry && "Text" in entry) return "Text";
  if (entry && "Narrative" in entry) return "Narrative";
  // No pre-existing key: fall back to the shared per-RMS mapping so reads/writes
  // here can't drift from buildNarrativeOnlySchema's key choice.
  return narrativeKey(rms);
};

interface NarrativeOnlyItemProps {
  index: number;
  text: string;
}

/**
 * One narrative block. Lives in its own component so each narrative owns its
 * loading/confirmation hook state.
 */
export const NarrativeOnlyItem: React.FC<NarrativeOnlyItemProps> = ({
  index,
  text,
}) => {
  const {
    report: { rms, allowEditing },
    save: { isSaving, updateFormData, saveChanges },
    session: { liveWebSocket, logUserActivity },
    chat: { sendMessageRef, setShowChat, available: chatAvailable },
    userProfile,
  } = useEditSection();
  const toast = useToast();
  const formData = useFormDataStore(
    (state) => state.formData,
  ) as EforceIncidentModel | null;

  const [loadingOperation, setLoadingOperation] =
    useState<LoadingOperation>(null);
  // Source-linked view is the default face of a cited narrative; "Edit text"
  // swaps to the textarea. Edits keep every citation whose sentence they
  // didn't touch (reconcileNarrativeEditorChange); only when the last marker
  // drops does hasCitedProvenance go false and the textarea simply stay.
  const { proseEditing, autoFocusEdit, enterEdit, latchEdit, exitEdit } =
    useSourceLinkedSwap(!allowEditing);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<
    (() => void) | null
  >(null);
  const [pendingOperationType, setPendingOperationType] =
    useState<OperationType>(null);
  const { rawValue, displayedValue } = getNarrativeEditorValue(text);
  const { enabled: citationsEnabled } = useNarrativeCitationsEnabled();
  const hasCitedProvenance =
    Boolean(citationsEnabled && rawValue) &&
    parseCitations(rawValue).groups.length > 0;

  // Agent path mirrors NarrativeWidget.convertIdToJsonPath
  // ("root_Narratives_0_Text" -> "root→Narrative→Narratives→0→Text").
  const narrativeKey = resolveNarrativeKey(
    formData?.Narratives?.[index],
    rms,
  );
  const narrativePath = `root→Narrative→Narratives→${index}→${narrativeKey}`;

  const { handleNarrativeRegeneration } = useNarrativeRegeneration({
    liveWebSocket,
    narrativeIndex: index,
    narrativePath,
    showError: toast.error,
    setLoadingOperation,
    chatSendMessage: sendMessageRef?.current ?? undefined,
    setShowChat,
  });

  const writeToStore = (value: string | undefined) => {
    if (!formData) return;
    const next = JSON.parse(JSON.stringify(formData)) as EforceIncidentModel;
    if (!Array.isArray(next.Narratives) || !next.Narratives[index]) return;
    next.Narratives[index][narrativeKey] = value ?? "";
    // Local store write only; persistence flows through the WebSocket save
    // (footer Save / Save & Exit, and the pre-regeneration save below).
    void updateFormData(next, false);
  };

  // Build a confirm-then-(save + regenerate) action for an AI operation. The
  // WebSocket save persists the latest narrative so the agent reads it.
  const runAIOperation = (
    op: Exclude<OperationType, null>,
    regenType: RegenerationType,
    args: RegenerationArgs | null,
    activityLabel: string,
  ) => {
    setPendingOperationType(op);
    setConfirmationAction(() => async () => {
      setLoadingOperation(op);
      await saveChanges();
      await handleNarrativeRegeneration(regenType, args);
      logUserActivity(activityLabel);
    });
    setConfirmationModalOpen(true);
  };

  const handleCorrectName = (nameToCorrect: string, correctName: string) => {
    if (!displayedValue) return;
    const updated = displayedValue.replace(
      new RegExp(nameToCorrect.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
      correctName,
    );
    writeToStore(
      reconcileNarrativeEditorChange(rawValue, displayedValue, updated),
    );
    logUserActivity("Corrected name in narrative");
  };

  const executeConfirmation = () => {
    if (confirmationAction) confirmationAction();
    setConfirmationModalOpen(false);
    setPendingOperationType(null);
  };

  // The rendered, source-linked block IS the field while provenance is
  // intact: always when editing is off, otherwise until "Edit text" swaps to
  // the textarea. AI operations fall through to the editor branch so its
  // loading overlay stays the single progress affordance. Anchors are
  // tooltip-only here — EditSection has no imperative citation-player bridge.
  const showSourceLinkedView =
    hasCitedProvenance && (!allowEditing || !proseEditing) && !loadingOperation;

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      {showSourceLinkedView ? (
        <SourceLinkedNarrative
          id={`narrative-only-${index}`}
          label={`Narrative ${index + 1}`}
          minRows={12}
          text={rawValue}
          {...(allowEditing
            ? {
                onEdit: enterEdit,
                editDisabled: isSaving,
                autoFocusEdit,
              }
            : {})}
        />
      ) : (
        <>
          {hasCitedProvenance && allowEditing && (
            <SourceLinkedEditBar
              onDone={exitEdit}
              disabled={loadingOperation !== null}
            />
          )}
          <NarrativeEditor
            id={`narrative-only-${index}`}
            value={displayedValue}
            onChange={(nextValue) =>
              writeToStore(
                reconcileNarrativeEditorChange(
                  rawValue,
                  displayedValue,
                  nextValue,
                ),
              )
            }
            disabled={!allowEditing || isSaving}
            readonly={!allowEditing}
            loadingOperation={loadingOperation}
            label={`Narrative ${index + 1}`}
            required={false}
            minRows={12}
            autoFocus={hasCitedProvenance && proseEditing}
            onFocus={latchEdit}
            // Narrative-only mode is a single prose block on a page that scrolls
            // on its own. Omitting maxRows lets the field grow to its full
            // content height — the whole narrative renders inside the parent
            // container with no internal scrollbar.
          />
        </>
      )}
      {/* AI tools route through the chat WebSocket; hide them when chat is
          unavailable (e.g. CHANGES_REQUESTED). The text field stays editable. */}
      {chatAvailable && allowEditing && (
        <AIAssistanceTools
          loadingOperation={loadingOperation}
          disabled={isSaving}
          onToFirstPerson={() =>
            runAIOperation(
              "toFirstPerson",
              "TO_FIRST_PERSON",
              null,
              "Converted narrative to first-person",
            )
          }
          onToThirdPerson={(officerName) =>
            runAIOperation(
              "toThirdPerson",
              "TO_THIRD_PERSON",
              { officerName },
              `Converted narrative to third-person (${officerName})`,
            )
          }
          onCorrectName={handleCorrectName}
          onRegenerateWithContext={(context) =>
            runAIOperation(
              "addContext",
              "ADD_CONTEXT",
              { additionalContext: context },
              "Regenerated narrative with additional context",
            )
          }
          userProfile={userProfile}
        />
      )}

      <NarrativeConfirmationModal
        open={confirmationModalOpen}
        onClose={() => {
          setConfirmationModalOpen(false);
          setPendingOperationType(null);
        }}
        onConfirm={executeConfirmation}
        operationType={pendingOperationType}
      />
    </Box>
  );
};

export const NarrativeOnlyEditor: React.FC = () => {
  const theme = useTheme();
  const {
    report: { allowEditing, isReportOwner, status },
    video: { data: videoData, loadStatus: videoLoadStatus },
    save: {
      generateFullReport,
      isGeneratingFull,
      generationStartedAt,
      generationTimeoutMs,
      submit,
      isSaving,
    },
  } = useEditSection();
  const formData = useFormDataStore((state) => state.formData);
  const narratives = extractNarrativeTexts(
    formData as Record<string, unknown> | null,
  );
  const [isVideoMinimized, setIsVideoMinimized] = useState(true);
  // Confirm + sign flow, mirroring the full editor: a per-narrative
  // confirmation plus the final attestation gate the "Finish Review" submit,
  // which hands off to /final-edits for signing.
  const [confirmed, setConfirmed] = useState(false);
  const [finalConfirmation, setFinalConfirmation] = useState(false);

  const canGenerate = allowEditing && isReportOwner;
  // "Generate Full Report" is only valid while the report is awaiting the
  // officer's review of the freshly generated narrative (PENDING_REVIEW).
  const canGenerateFullReport =
    canGenerate && status === ReportStatus.PENDING_REVIEW;
  const hasNarrative = narratives.length > 0;

  return (
    <Box sx={{ overscrollBehavior: "contain" }}>
      {/* Video Preview — parity with the full editor */}
      {videoData ? (
        <Box sx={{ mb: 2 }}>
          <VideoPreview
            videoData={videoData}
            isMinimized={isVideoMinimized}
            onToggleMinimize={() => setIsVideoMinimized((prev) => !prev)}
          />
        </Box>
      ) : videoLoadStatus === "loading" ? (
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
        </Box>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Video evidence could not be loaded.
        </Alert>
      )}

      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Narrative
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Review and edit the narrative below. When you're ready, generate the
          full report to extract the remaining structured details.
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* While the full report generates, the report is locked: the
            narrative, AI tools, and the confirm/sign controls are all replaced
            by a blocking progress timer so it can't be edited or read mid-run. */}
        {isGeneratingFull && generationStartedAt !== null ? (
          <FullReportGenerationProgress
            startedAt={generationStartedAt}
            timeoutMs={generationTimeoutMs}
          />
        ) : (
          <>
            {!hasNarrative ? (
              <Alert severity="info">
                No narrative is available for this report.
              </Alert>
            ) : (
              <Stack spacing={4}>
                {narratives.map((n) => (
                  <NarrativeOnlyItem key={n.index} index={n.index} text={n.text} />
                ))}
              </Stack>
            )}

            {/* Expand to a full report — the alternative path to signing the
                narrative as-is. */}
            {canGenerateFullReport && (
              <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => void generateFullReport()}
                  loading={isGeneratingFull}
                  sx={{
                    minWidth: 260,
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontSize: "1rem",
                  }}
                >
                  {isGeneratingFull
                    ? "Generating Full Report…"
                    : "Generate Full Report"}
                </Button>
              </Box>
            )}

            {/* Per-narrative confirmation (mirrors the full editor's
                per-section confirmation). */}
            {canGenerate && hasNarrative && (
              <Box sx={{ mt: 3, textAlign: "center" }}>
                <ConfirmationCheckbox
                  isConfirmed={confirmed}
                  isDisabled={isSaving}
                  onChange={setConfirmed}
                />
              </Box>
            )}

            {/* Confirm + sign the narrative-only report, exactly like full
                mode: this hands off to /final-edits for signing. */}
            {hasNarrative && (
              <FormSubmitArea
                currentSection={0}
                totalSections={1}
                allowEditing={allowEditing}
                isReportOwner={isReportOwner}
                areAllPagesConfirmed={confirmed}
                finalConfirmation={finalConfirmation}
                isSaving={isSaving}
                setFinalConfirmation={setFinalConfirmation}
                onSubmit={() => void submit()}
                // Single-page narrative — no "previous pages" to attest to, so
                // the page confirmation above is the only gate.
                showFinalConfirmation={false}
              />
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};
